// SSO-first registration: invite_id from ?invite_id= or decrypted ?e=; email signup → /emailRegister
import React, { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom';
import { Alert, Box, Divider, Link, Paper, Typography } from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { decryptRegistrationParams } from '../helpers/encryptionUtils';
import AuthBrandedLayout from '../components/auth/AuthBrandedLayout';
import SsoProviderButtons from '../components/auth/SsoProviderButtons';

const Register: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAuthenticated } = useAuth();
  const [ssoError, setSsoError] = useState('');
  const [inviteId, setInviteId] = useState<string | undefined>(undefined);
  const [resolvingInvite, setResolvingInvite] = useState(false);
  const [inviteResolveError, setInviteResolveError] = useState('');

  const encryptedInvite = searchParams.get('e');
  const plainInviteId = searchParams.get('invite_id');

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/supportSpace');
    }
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    if (plainInviteId) {
      setInviteId(plainInviteId);
      setInviteResolveError('');
      return;
    }

    if (!encryptedInvite) {
      setInviteId(undefined);
      setInviteResolveError('');
      return;
    }

    let cancelled = false;
    setResolvingInvite(true);
    setInviteResolveError('');
    decryptRegistrationParams(encryptedInvite)
      .then((params) => {
        if (!cancelled) {
          setInviteId(params.inviteCode);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setInviteResolveError('Invalid or expired invitation link. Please use the link from your email.');
          setInviteId(undefined);
        }
      })
      .finally(() => {
        if (!cancelled) setResolvingInvite(false);
      });

    return () => {
      cancelled = true;
    };
  }, [plainInviteId, encryptedInvite]);

  const emailRegisterHref = useMemo(() => {
    const qs = searchParams.toString();
    return qs ? `/emailRegister?${qs}` : '/emailRegister';
  }, [searchParams]);

  const ssoReady = !resolvingInvite && !inviteResolveError;

  return (
    <AuthBrandedLayout>
      <Paper
        elevation={4}
        sx={{
          p: 4,
          maxWidth: 440,
          width: '100%',
          borderRadius: 2,
          bgcolor: 'background.paper',
        }}
      >
        <Typography variant="h5" component="h1" align="center" sx={{ fontWeight: 700, mb: 1 }}>
          Create your account
        </Typography>
        <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 3 }}>
          Sign up with Google or Microsoft, or use your organization email.
        </Typography>

        {inviteResolveError ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {inviteResolveError}
          </Alert>
        ) : null}

        {resolvingInvite ? (
          <Alert severity="info" sx={{ mb: 2 }}>
            Verifying your invitation…
          </Alert>
        ) : null}

        {ssoError ? (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setSsoError('')}>
            {ssoError}
          </Alert>
        ) : null}

        <SsoProviderButtons
          allowClick={ssoReady}
          inviteId={inviteId}
          onError={setSsoError}
        />

        <Box sx={{ display: 'flex', alignItems: 'center', my: 3 }}>
          <Divider sx={{ flex: 1 }} />
          <Typography variant="body2" color="text.secondary" sx={{ px: 2 }}>
            OR
          </Typography>
          <Divider sx={{ flex: 1 }} />
        </Box>

        <Box
          sx={{
            border: '2px dashed',
            borderColor: 'divider',
            borderRadius: 2,
            p: 2,
            textAlign: 'center',
          }}
        >
          <Link component={RouterLink} to={emailRegisterHref} underline="hover" sx={{ fontWeight: 600 }}>
            Sign up with email
          </Link>
        </Box>

        <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 2 }}>
          Already have an account?{' '}
          <Link component={RouterLink} to="/login" underline="hover" fontWeight={600}>
            Log in
          </Link>
        </Typography>
      </Paper>
    </AuthBrandedLayout>
  );
};

export default Register;
